import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import VideocamIcon from '@material-ui/icons/Videocam';
import MicIcon from '@material-ui/icons/Mic';
import LayersIcon from '@material-ui/icons/Layers';
import ArticleIcon from '@material-ui/icons/Article';
import LanguageIcon from '@material-ui/icons/Language';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import EventIcon from '@material-ui/icons/Event';
import ShareIcon from '@material-ui/icons/Share';
import StarIcon from '@material-ui/icons/Star';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import handleOpenWhatsApp from 'services/whatsappService';
import {
  PACKAGE_CATEGORIES,
  commercialProducts,
  commercialBundles,
  packagesPageCopy,
} from '../../../data/commercialPackagesData';
import { getTnoHeroGradient } from '../../../theme/tnoBrandColors';

const ICON_MAP = {
  broadcast: LiveTvIcon,
  mic: MicIcon,
  layers: LayersIcon,
  article: ArticleIcon,
  web: LanguageIcon,
  ugc: VideocamIcon,
  camera: CameraAltIcon,
  event: EventIcon,
  share: ShareIcon,
  studio: VideocamIcon,
};

function ProductIcon({ type, color }) {
  const Icon = ICON_MAP[type] || LiveTvIcon;
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(color, 0.14),
        color,
        mb: 1.5,
      }}
    >
      <Icon />
    </Box>
  );
}

ProductIcon.propTypes = {
  type: PropTypes.string,
  color: PropTypes.string,
};

const LandingMediaKitPaquetes = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const isDark = themeMode === 'dark';
  const [category, setCategory] = useState('all');
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';

  const filteredProducts = useMemo(() => {
    if (category === 'all' || category === 'bundles') {
      return commercialProducts;
    }
    return commercialProducts.filter((p) => p.category === category);
  }, [category]);

  const showBundles = category === 'all' || category === 'bundles';
  const productCount = commercialProducts.length;
  const bundleCount = commercialBundles.length;

  const heroBackground = getTnoHeroGradient(primary, secondary);
  const cardBg = isDark ? alpha('#fff', 0.06) : '#fff';
  const cardBorder = isDark ? alpha('#fff', 0.1) : alpha(primary, 0.1);
  const muted = isDark ? 'rgba(248,250,252,0.72)' : 'rgba(15,23,42,0.65)';

  const openWhatsApp = (productName) => {
    const msg = [
      'Hola, me interesa armar una propuesta comercial en TNO Network.',
      productName ? `Producto / paquete de referencia: ${productName}` : '',
      '',
      typeof window !== 'undefined' ? window.location.href : '',
    ]
      .filter(Boolean)
      .join('\n');
    handleOpenWhatsApp(undefined, msg);
  };

  return (
    <Box
      sx={{
        pb: 8,
        background: `linear-gradient(180deg, ${alpha(primary, 0.04)} 0%, ${alpha(secondary, 0.06)} 12%, transparent 28%)`,
      }}
    >
      <Container>
        <Box pt={{ xs: 3, md: 4 }} pb={3}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            color="primary"
            sx={{ mb: 3, borderRadius: 999 }}
          >
            Volver al media kit
          </Button>

          <Box
            sx={{
              borderRadius: { xs: 3, md: 4 },
              overflow: 'hidden',
              mb: 4,
              background: heroBackground,
              color: '#fff',
              px: { xs: 2.5, md: 4 },
              py: { xs: 4, md: 5 },
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 200,
                height: 200,
                borderRadius: '50%',
                top: -50,
                right: -30,
                bgcolor: 'rgba(255,255,255,0.1)',
                pointerEvents: 'none',
              }}
            />
            <Box
              component="img"
              src="/images/logos/logo_white.PNG"
              alt="TNO Network"
              sx={{
                height: { xs: 44, md: 52 },
                width: 'auto',
                display: 'block',
                mb: 2,
                filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.2))',
              }}
            />
            <Typography
              sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, opacity: 0.9, fontSize: '0.8rem' }}
            >
              {packagesPageCopy.heroOverline}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.1rem', md: '2.85rem' },
                lineHeight: 1.08,
                mt: 1,
                mb: 2,
                maxWidth: 720,
              }}
            >
              {packagesPageCopy.heroTitle}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.65, maxWidth: 640, opacity: 0.92, mb: 3 }}>
              {packagesPageCopy.heroSubtitle}
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item>
                <Chip
                  icon={<ShoppingCartIcon />}
                  label={`${productCount} productos`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
                />
              </Grid>
              <Grid item>
                <Chip
                  icon={<StarIcon />}
                  label={`${bundleCount} paquetes integrados`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
            {PACKAGE_CATEGORIES.map((cat) => (
              <Chip
                key={cat.id}
                label={cat.label}
                onClick={() => setCategory(cat.id)}
                color={category === cat.id ? 'primary' : 'default'}
                variant={category === cat.id ? 'default' : 'outlined'}
                sx={{
                  fontWeight: category === cat.id ? 700 : 500,
                  borderRadius: 999,
                  px: 0.5,
                }}
              />
            ))}
          </Box>

          {showBundles && (
            <Box mb={5}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                Paquetes integrados
              </Typography>
              <Typography sx={{ color: muted, mb: 3, maxWidth: 640 }}>
                Combinaciones listas para cotizar, inspiradas en propuestas LED Home, GPM Eventos y patrocinios de programa.
              </Typography>
              <Grid container spacing={3}>
                {commercialBundles.map((bundle) => (
                  <Grid item xs={12} md={4} key={bundle.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        border: bundle.highlighted
                          ? `2px solid ${bundle.accent}`
                          : `1px solid ${cardBorder}`,
                        bgcolor: cardBg,
                        boxShadow: bundle.highlighted
                          ? `0 12px 40px ${alpha(bundle.accent, 0.22)}`
                          : `0 4px 24px ${alpha(primary, 0.06)}`,
                        overflow: 'hidden',
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {bundle.badge ? (
                          <Chip
                            label={bundle.badge}
                            size="small"
                            sx={{
                              mb: 1.5,
                              fontWeight: 700,
                              bgcolor: bundle.accent,
                              color: '#fff',
                              height: 'auto',
                              maxWidth: '100%',
                              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35, py: 0.5 },
                            }}
                          />
                        ) : null}
                        <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 0.5 }}>{bundle.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
                          <Typography component="span" sx={{ fontWeight: 900, fontSize: '2.25rem', color: bundle.accent }}>
                            ${bundle.price}
                          </Typography>
                          <Typography variant="body2" sx={{ color: muted }}>
                            {bundle.priceNote}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: muted, fontSize: '0.95rem', mb: 2, lineHeight: 1.55 }}>
                          {bundle.summary}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', mb: 2 }}>
                          {bundle.items.map((item) => (
                            <Box
                              component="li"
                              key={item.label}
                              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18, color: bundle.accent, mt: 0.25, flexShrink: 0 }} />
                              <Typography variant="body2">
                                <strong>{item.label}</strong>
                                {item.value ? ` — ${item.value}` : ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', color: muted, mb: 2, lineHeight: 1.5 }}>
                          Ideal para: {bundle.idealFor}
                        </Typography>
                        <Button
                          fullWidth
                          variant={bundle.highlighted ? 'contained' : 'outlined'}
                          onClick={() => openWhatsApp(bundle.name)}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 700,
                            ...(bundle.highlighted && { bgcolor: bundle.accent, '&:hover': { bgcolor: bundle.accent } }),
                          }}
                        >
                          Cotizar {bundle.name}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {category !== 'bundles' && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                Productos unitarios
              </Typography>
              <Typography sx={{ color: muted, mb: 3, maxWidth: 640 }}>
                Arma tu plan à la carte: micros, rotativas, Digest, coberturas, UGC y presencia en web.
              </Typography>
              <Grid container spacing={2.5}>
                {filteredProducts.map((product) => (
                  <Grid item xs={12} sm={6} lg={4} key={product.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        border: `1px solid ${cardBorder}`,
                        bgcolor: cardBg,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 32px ${alpha(product.accent, 0.15)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <ProductIcon type={product.icon} color={product.accent} />
                        <Chip
                          label={product.frequency}
                          size="small"
                          sx={{
                            mb: 1.5,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: alpha(product.accent, 0.12),
                            color: product.accent,
                          }}
                        />
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5, lineHeight: 1.3 }}>
                          {product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.75rem', color: product.accent }}>
                            ${product.price}
                          </Typography>
                          <Typography variant="caption" sx={{ color: muted }}>
                            {product.priceNote}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: muted, fontSize: '0.9rem', mb: 2, lineHeight: 1.55 }}>
                          {product.summary}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
                          {product.includes.map((line) => (
                            <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5, color: muted }}>
                              {line}
                            </Typography>
                          ))}
                        </Box>
                        {Array.isArray(product.anchorPrograms) && product.anchorPrograms.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: product.accent, display: 'block', mb: 0.75 }}>
                              Programas de referencia
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {product.anchorPrograms.map((prog) => (
                                <Chip
                                  key={prog.showSlug || prog.name}
                                  label={prog.name}
                                  size="small"
                                  sx={{
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(product.accent, 0.1),
                                    color: product.accent,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => openWhatsApp(product.name)}
                          sx={{ borderRadius: 999, fontWeight: 600 }}
                        >
                          Incluir en propuesta
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Box
            sx={{
              mt: 6,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(primary, 0.08)} 0%, ${alpha(secondary, 0.12)} 100%)`,
              border: `1px solid ${cardBorder}`,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              ¿Necesitas una propuesta a medida?
            </Typography>
            <Typography sx={{ color: muted, mb: 2, maxWidth: 520, mx: 'auto' }}>
              {packagesPageCopy.disclaimer}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => openWhatsApp()}
                sx={{ borderRadius: 999, fontWeight: 700, px: 4 }}
              >
                {packagesPageCopy.ctaLabel}
              </Button>
              <Button
                component={RouterLink}
                to="/programacion"
                variant="outlined"
                color="primary"
                size="large"
                sx={{ borderRadius: 999, fontWeight: 600 }}
              >
                Ver programación por nichos
              </Button>
            </Box>
          </Box>

          <PriceVatDisclaimer sx={{ mt: 4 }} />
        </Box>
      </Container>
    </Box>
  );
};

LandingMediaKitPaquetes.propTypes = {
  themeMode: PropTypes.string,
};

LandingMediaKitPaquetes.defaultProps = {
  themeMode: 'light',
};

export default LandingMediaKitPaquetes;
